/**
 * useReservationFolio — TanStack Query hook over
 * `GET /organizations/{slug}/reservations/{reservationId}/folio` (SCRUM-336).
 *
 * Mirrors `use-reservation-detail.ts` EXACTLY: the same five-branch status
 * union `loading | ready | forbidden | notFound | error`, the same
 * Symbol-sentinel posture, the same org-scoped queryKey.
 *
 * The folio is the authoritative merchant-of-record source (its
 * `collectionModel`) plus the cash summary — the ledger's Region B, gated
 * by `payments.folios.read`. A 403 → `forbidden` (the caller lost the
 * folio-read grant since `/auth/me` cached; the tab renders an honest
 * partial with the line-item region only). A 404 → `notFound`
 * (honest-empty: no folio yet for a pre-confirmed / block reservation,
 * anti-enumeration) — a DISTINCT, non-error state, NOT the generic Retry
 * affordance.
 *
 *   - reads the org slug via `useCurrentOrg()` (RLS-scoped per CLAUDE.md
 *     non-negotiable §1)
 *   - `queryKey = [...bookingQueryKeys.folio(id), orgSlug]`
 *   - `enabled: orgSlug !== null && reservationId != null`
 *   - `retry: false`
 *   - the response envelope is `{ data: FolioReadModelDto }` — the `ready`
 *     result unwraps `result.data.data`
 *   - every branch carries a `refetch` function
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api-client.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import type { FolioReadModelDto } from './types.js';
import { bookingQueryKeys } from './queryKeys.js';

/**
 * 403 sentinel — the caller lacks `payments.folios.read`. The ledger renders
 * an honest partial (line-item region only; no cash summary / badge). Own
 * module-level Symbol, distinct from every other hook's `FORBIDDEN`.
 */
export const FOLIO_FORBIDDEN = Symbol('reservation-folio:forbidden');

/**
 * 404 sentinel — no folio yet (pre-confirmed / never-created; anti-enum). A
 * distinct, non-error state: the tab renders honest-empty for Region B.
 */
export const FOLIO_NOT_FOUND = Symbol('reservation-folio:notFound');

export type UseReservationFolioResult =
  | {
      readonly status: 'loading';
      readonly data?: undefined;
      readonly refetch: () => void;
    }
  | {
      readonly status: 'ready';
      /** The folio read-model, unwrapped from the `{ data }` envelope. */
      readonly data: FolioReadModelDto;
      readonly refetch: () => void;
    }
  | {
      readonly status: 'forbidden';
      readonly data?: undefined;
      readonly refetch: () => void;
    }
  | {
      readonly status: 'notFound';
      readonly data?: undefined;
      readonly refetch: () => void;
    }
  | {
      readonly status: 'error';
      readonly data?: undefined;
      readonly refetch: () => void;
    };

export function useReservationFolio(
  reservationId: string | null,
): UseReservationFolioResult {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;

  const query = useQuery({
    // `folio(id)` requires a string; when id is null the query is disabled
    // so the key is never used — an empty string keeps the tuple type stable.
    queryKey: [...bookingQueryKeys.folio(reservationId ?? ''), orgSlug] as const,
    enabled: orgSlug !== null && reservationId != null,
    retry: false,
    queryFn: async () => {
      const result = await apiClient.GET(
        '/organizations/{slug}/reservations/{reservationId}/folio',
        {
          params: {
            path: {
              slug: orgSlug as string,
              reservationId: reservationId as string,
            },
          },
        },
      );
      if (result.response.status === 403) {
        return FOLIO_FORBIDDEN;
      }
      if (result.response.status === 404) {
        return FOLIO_NOT_FOUND;
      }
      if (!result.response.ok || result.data === undefined) {
        throw new Error(
          `GET /organizations/${orgSlug}/reservations/${reservationId}/folio failed with status ${result.response.status}`,
        );
      }
      return result.data;
    },
  });

  const refetch = (): void => {
    void query.refetch();
  };

  if (orgSlug === null || reservationId == null || query.isPending) {
    return { status: 'loading', refetch };
  }
  if (query.isError) {
    return { status: 'error', refetch };
  }
  if (query.data === FOLIO_FORBIDDEN) {
    return { status: 'forbidden', refetch };
  }
  if (query.data === FOLIO_NOT_FOUND) {
    return { status: 'notFound', refetch };
  }

  const envelope = query.data;
  return {
    status: 'ready',
    data: envelope.data,
    refetch,
  };
}
